/**
 * BackupActivityPanel (Spec 201)
 *
 * The "Backup activity (N days)" succeeded-vs-failed trend chart, self-contained (fetches its
 * own data) so the SAME diagram can sit on the Backup Command Center AND the Admin / Operator
 * dashboards. Pulls GET /api/v1/backup/dashboard/trend?days=N and renders the shared recharts
 * BackupActivityTrend inside a DashboardPanel.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { DashboardPanel, LoadingState, ErrorState } from '@/components/ui';
import { backupDashboardApi } from '../../api/dashboard';
import { BackupActivityTrend } from './CommandCenterCharts';

export const BACKUP_TREND_DAYS = 14;

export const BackupActivityPanel: React.FC<{ days?: number }> = ({ days = BACKUP_TREND_DAYS }) => {
  const trend = useQuery({
    queryKey: ['backup', 'dash', 'trend', days],
    queryFn: () => backupDashboardApi.trend(days),
  });
  return (
    <DashboardPanel
      title={`Backup activity (${days} days)`}
      titleInfo="Backup runs per day over the window, stacked by outcome (succeeded / partial / failed / cancelled) across lakehouse + warehouse — a quick read on daily backup reliability."
      subtitle="Succeeded vs failed runs per day — lakehouse + warehouse"
    >
      {trend.isLoading ? (
        <LoadingState rows={3} />
      ) : trend.isError ? (
        <ErrorState onRetry={() => trend.refetch()} />
      ) : (
        <BackupActivityTrend data={trend.data ?? []} />
      )}
    </DashboardPanel>
  );
};

export default BackupActivityPanel;
